import * as React from 'react'
import * as PropTypes from 'prop-types'
import { css, Rule } from 'glamor'

import { isKey } from '../../utils'
import { useTheme } from '../../theme'
import { UiVariant } from '../../types'

const variants = [
  'primary',
  'secondary',
  'positive',
  'negative',
  'alert',
  'cta',
  'brand',
  'dim',
  'normal',
  'link',
  'white',
  'inherit',
]
const fontWeights = ['inherit', 'light', 'normal', 'medium', 'semibold', '600', '500', '400', '300', 600, 500, 400, 300]
const fontStyles = ['inherit', 'normal', 'italic']
const headings = ['main', 'normal', 'small', 'xSmall', 'xxSmall', 'xxxSmall']
const sizes = ['small', 'medium', 'large', 'huge']

interface IStyledTextProps {
  children: React.ReactNode
  variant: UiVariant | 'dim' | 'normal' | 'link' | 'white' | 'inherit'
  component?: string | React.ComponentType<{}>
  weight?:
    | 'inherit'
    | 'light'
    | 'normal'
    | 'medium'
    | 'semibold'
    | '600'
    | '500'
    | '400'
    | '300'
    | 600
    | 500
    | 400
    | 300
  size?: 'inherit' | 'small' | 'medium' | 'large' | 'huge'
  heading?: 'main' | 'normal' | 'small' | 'xSmall' | 'xxSmall' | 'xxxSmall'
  fontStyle?: 'inherit' | 'normal' | 'italic'
  style?: Rule
  center?: boolean
  uppercase?: boolean
  lowercase?: boolean
  capitalize?: boolean
  monospace?: boolean
  nowrap?: boolean
  decoration?: string
}

export function StyledText({
  variant,
  weight,
  size,
  fontStyle,
  component: StyledTextComponent,
  style,
  center,
  uppercase,
  lowercase,
  capitalize,
  monospace,
  decoration,
  heading,
  nowrap,
  ...rest
}: IStyledTextProps) {
  const theme = useTheme()

  if (!StyledTextComponent) {
    return null
  }

  const baseStyles = {
    primary: {
      color: theme.colors.accents.primary,
    },
    secondary: {
      color: theme.colors.accents.secondary,
    },
    negative: {
      color: theme.colors.accents.negative,
    },
    positive: {
      color: theme.colors.accents.positive,
    },
    alert: {
      color: theme.colors.accents.alert,
    },
    cta: {
      color: theme.colors.accents.cta,
    },
    brand: {
      color: theme.colors.accents.brand,
    },
    link: {
      color: theme.colors.accents.primary,
      fontWeight: '500',
      cursor: 'pointer',
      ':hover': {
        textDecoration: 'underline',
      },
      ...theme.settings.focusStyles,
    },
    dim: {
      color: theme.colors.text.dimmed,
    },
    normal: {
      color: theme.colors.text.normal,
    },
    white: {
      color: theme.colors.text.light,
    },
    inherit: {
      color: 'inherit',
    },
  }

  return (
    <StyledTextComponent
      {...css(
        baseStyles[variant],
        isKey(theme.settings.typography.weights, weight)
          ? { fontWeight: theme.settings.typography.weights[weight] }
          : { fontWeight: weight },
        {
          fontStyle,
        },
        size &&
          isKey(theme.settings.typography.sizes, size) && {
            fontSize: theme.settings.typography.sizes[size] || size,
          },
        isKey(theme.typography.headings, heading) && theme.typography.headings[heading],
        center && { textAlign: 'center' },
        uppercase && { textTransform: 'uppercase' },
        lowercase && { textTransform: 'lowercase' },
        capitalize && { textTransform: 'capitalize' },
        nowrap && { whiteSpace: 'nowrap' },
        !!decoration && { textDecoration: decoration },
        monospace && { fontFamily: theme.settings.typography.fontFamilies.monospace },
        style
      )}
      {...rest}
    />
  )
}

StyledText.propTypes = {
  variant: PropTypes.oneOf(variants),
  component: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  weight: PropTypes.oneOf(fontWeights),
  size: PropTypes.oneOf(['inherit', ...sizes]),
  heading: PropTypes.oneOf(headings),
  fontStyle: PropTypes.oneOf(fontStyles),
  style: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  center: PropTypes.bool,
  uppercase: PropTypes.bool,
  lowercase: PropTypes.bool,
  capitalize: PropTypes.bool,
  monospace: PropTypes.bool,
  nowrap: PropTypes.bool,
  decoration: PropTypes.string,
}

StyledText.defaultProps = {
  size: 'inherit',
  weight: 'inherit',
  component: 'span',
  variant: 'inherit',
  fontStyle: 'inherit',
}

StyledText.displayName = 'StyledText'

StyledText.VARIANTS = variants
StyledText.SIZES = ['inherit', ...sizes]
StyledText.FONT_WEIGHTS = fontWeights
StyledText.FONT_STYLES = fontStyles
StyledText.HEADINGS = headings
